import { useCallback, useEffect, useRef, useState } from 'react';
import type { WidgetTheme } from '@intent/shared';
import type { ChatClient, ChatState } from '../core/chat-client';
import { playIncomingChime } from '../core/sound';
import { Avatar } from './Avatar';
import { ChatPanel } from './ChatPanel';
import { Popup } from './Popup';
import { PresenceLine } from './PresenceLine';
import { useTabBadge } from './use-tab-badge';

/**
 * Widget shell.
 *
 * Owns only presentation state — open/closed, unread count, whether the
 * proactive teaser is showing. All conversation state lives in `ChatClient`,
 * which is framework-agnostic; this subscribes to it.
 */
/** Most teaser cards shown at once; older unread ones fold into the badge. */
const MAX_TEASERS = 3;

export function App({ client, theme }: { client: ChatClient; theme: WidgetTheme }) {
  const [state, setState] = useState<ChatState>(client.getState);
  const [isOpen, setIsOpen] = useState(false);
  // Teaser cards the visitor has closed individually. Kept as ids so each new
  // message still appears even after earlier ones were dismissed.
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());

  // How many messages the visitor has already accounted for. Advancing this is
  // what marks messages read — done by opening or closing the chat. Everything
  // past it is "unread".
  const seenCount = useRef(state.messages.length);
  // Length last observed, so a genuinely new message can be told apart from a
  // re-render and chime exactly once.
  const observedCount = useRef(state.messages.length);

  useEffect(() => client.subscribe(setState), [client]);

  const messages = state.messages;

  // Chime once per genuinely new incoming message.
  useEffect(() => {
    const latest = messages[messages.length - 1];
    const isNew = messages.length > observedCount.current;
    observedCount.current = messages.length;

    if (isNew && latest && latest.role !== 'VISITOR') {
      playIncomingChime();
    }
  }, [messages]);

  /**
   * Unread proactive messages, freshest last.
   *
   * These become teaser cards. Rendering each new message as its own card —
   * rather than replacing the previous card's text in place — means a new
   * message visibly *rises in* as a distinct card, so the visitor can tell
   * something arrived. Dismissing one card leaves the others; opening the chat
   * clears them all.
   */
  const pending = messages
    .slice(seenCount.current)
    .filter((message) => message.role !== 'VISITOR' && !dismissedIds.has(message.id));

  const teaserCards = isOpen ? [] : pending.slice(-MAX_TEASERS);
  const unreadCount = isOpen ? 0 : pending.length;

  // Pull the visitor back if they've tabbed away with a message waiting.
  useTabBadge(unreadCount);

  const founderName = theme.founderName.trim() || state.projectName;

  const settle = useCallback(() => {
    // Mark everything up to now read, and forget per-card dismissals so a
    // future message starts with a clean slate.
    seenCount.current = messages.length;
    setDismissedIds(new Set());
  }, [messages.length]);

  const open = useCallback(() => {
    setIsOpen(true);
    settle();
  }, [settle]);

  const close = useCallback(() => {
    setIsOpen(false);
    settle();
  }, [settle]);

  const dismissTeaser = useCallback((id: string) => {
    setDismissedIds((current) => new Set(current).add(id));
  }, []);

  return (
    <>
      {state.activePopup && <Popup client={client} state={state} />}
      <div className="root" data-position={theme.position}>
        <div className="stack">
        {isOpen && (
          <ChatPanel
            client={client}
            state={state}
            theme={theme}
            founderName={founderName}
            onClose={close}
          />
        )}

        {teaserCards.map((message) => (
          <div key={message.id} className="teaser" role="button" tabIndex={0} onClick={open}>
            <div className="teaser-header">
              <Avatar name={founderName} src={theme.founderAvatarUrl || undefined} size={40} />
              <div className="teaser-id">
                <span className="teaser-name">{founderName}</span>
                <PresenceLine
                  role={theme.founderRole}
                  availabilityState={state.availabilityState}
                />
              </div>
              <button
                className="teaser-dismiss"
                aria-label="Dismiss"
                onClick={(event) => {
                  event.stopPropagation();
                  dismissTeaser(message.id);
                }}
              >
                ×
              </button>
            </div>
            <span className="teaser-text">{message.content}</span>
          </div>
        ))}

        <button
          className="bubble"
          onClick={isOpen ? close : open}
          aria-label={isOpen ? 'Close chat' : 'Open chat'}
          aria-expanded={isOpen}
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
          {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
        </button>
        </div>
      </div>
    </>
  );
}

function ChatIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
